import { useEffect, useState } from 'react';
import { FileResponse, FileSource } from '../interfaces/FileResponse';
import { StringHelper } from '../infra/stringHelper.component';
import { useCollectionService } from '../../services/collection.service';
import pdfImg from "../../assets/img/pdf.png";

export interface SheetSource {
    src?: string,
    extension?: string,
    blob?: Blob,
    pdfLabel?: string,
}

// Sheets are shown in more than one place (slide + stage mode), so keep each download once per serverPath
const cache = new Map<string, Promise<SheetSource>>();

const cacheKey = (item: FileResponse) => `${item.source}|${item.serverPath || item.address}`;

async function loadSource(item: FileResponse, getFile: (path: string) => Promise<any>): Promise<SheetSource> {
    switch (item.source) {
        case FileSource.Dropbox: {
            const downloadedContent: any = await getFile(item.serverPath);

            if (item.extension !== 'pdf') {
                const image = await StringHelper.blobToBase64Image(downloadedContent, item.extension);

                return { src: image, extension: item.extension };
            }

            const src = navigator.pdfViewerEnabled
                ? await StringHelper.blobToBase64Pdf(downloadedContent)
                : pdfImg;

            return { src, extension: item.extension, blob: downloadedContent, pdfLabel: item.address };
        }
        default: {
            const isPdf = item.address.split('?')[0].toLowerCase().endsWith('.pdf');

            if (isPdf)
                return { src: navigator.pdfViewerEnabled ? item.address : pdfImg, extension: 'pdf' };

            return { src: item.address };
        }
    }
}

export function useSheetSource(item: FileResponse): SheetSource {
    const [source, setSource] = useState<SheetSource>({});

    const collectionService = useCollectionService();

    useEffect(() => {
        let active = true;
        const key = cacheKey(item);

        let pending = cache.get(key);
        if (!pending) {
            pending = loadSource(item, path => collectionService.getFile(path));
            cache.set(key, pending);

            // Let a failed download be retried next time
            pending.catch(() => cache.delete(key));
        }

        pending
            .then(result => { if (active) setSource(result); })
            .catch(() => { });

        return () => { active = false; };
    }, [item, collectionService]);

    return source;
}

export const isPdfItem = (item: FileResponse) => item.source === FileSource.Dropbox
    ? item.extension === 'pdf'
    : item.address.split('?')[0].toLowerCase().endsWith('.pdf');

export const isPdfShownInline = (source: SheetSource) => !!source.src && source.extension === 'pdf' && !!navigator.pdfViewerEnabled;
